import moment from 'moment';

import { filterVisibility, isInHiddenWindow } from './utils';

/**
 * Off-shelf state for a dish, as the rest of the platform defines it.
 *
 * A dish can be off the menu for two unrelated reasons, and the merchant needs to tell them apart:
 *
 *  - *Live* — an entry under the realtime-database node `inactiveMenus/{restaurantId}`. The POS writes
 *    one when someone 86s a dish, and the server writes one when a stock limit runs out
 *    (`dishQuantity.js`). This is the state the admin portal could not see at all until now: it never
 *    reaches Firestore unless the POS happens to send `menuId` and `categoryId` along with it.
 *  - *Scheduled* — the per-item visibility flags the Visibility dialog writes to Firestore (`active`,
 *    `hidden_until`, `hideDays`, `active_begin`/`active_end`, `activeWeekDays`, and on categories
 *    `hidden_begin`/`hidden_end`, `hiddenWeekDays`).
 *
 * The rules here are the server's, kept deliberately identical so the portal agrees with what the
 * customer actually sees: `normalizeInactiveEntries` and `entryApplies` mirror
 * `functions/APIs/order/orderAvailability.js`, and the sold-out/taken-off split mirrors its `reasonFor`
 * — a sell-out entry is built from the dish's stock document so it always carries `quantity`, a manual
 * off-shelve never does. The scheduled half defers to `filterVisibility`, which is what the online menu
 * itself filters on.
 */

export const OFF_SHELF_SOLD_OUT = 'sold_out';
export const OFF_SHELF_TAKEN_OFF = 'off_shelf';

/**
 * Flattens the raw `dishIds` child into `{menuItemId, effectiveDate, ...}` entries.
 *
 * The realtime database hands back an integer-keyed node as an array padded with `null` for every
 * missing index, or as an object once the list is sparse enough. Older nodes stored a bare array of
 * ids, and sold-out entries written by the stock path can carry `dishId` rather than `menuItemId`.
 */
export const normalizeInactiveEntries = (dishIds) => {
  const raw = Array.isArray(dishIds) ? dishIds : Object.values(dishIds || {});

  return raw.reduce((entries, entry) => {
    if (typeof entry === 'string') {
      if (entry.trim() !== '') {
        entries.push({ menuItemId: entry, effectiveDate: '' });
      }
      return entries;
    }

    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return entries;
    }

    const menuItemId = entry.menuItemId || entry.dishId;
    if (!menuItemId || typeof menuItemId !== 'string') {
      return entries;
    }

    entries.push({
      ...entry,
      menuItemId,
      effectiveDate: typeof entry.effectiveDate === 'string' ? entry.effectiveDate : '',
    });
    return entries;
  }, []);
};

/** No date means "off until someone turns it back on"; today means "off until tomorrow". */
export const entryApplies = (effectiveDate, today) => !effectiveDate || effectiveDate === today;

/** Indexes the entries that are still in force today by dish id. */
export const buildOffShelfMap = (dishIds, today) => {
  const applying = new Map();

  normalizeInactiveEntries(dishIds).forEach((entry) => {
    if (entryApplies(entry.effectiveDate, today) && !applying.has(entry.menuItemId)) {
      applying.set(entry.menuItemId, entry);
    }
  });

  return applying;
};

export const offShelfReason = (entry) =>
  Object.prototype.hasOwnProperty.call(entry, 'quantity') ? OFF_SHELF_SOLD_OUT : OFF_SHELF_TAKEN_OFF;

export const offShelfLabel = (live) => {
  const noun = live.reason === OFF_SHELF_SOLD_OUT ? 'Sold out' : 'Off shelf';
  return live.until ? `${noun} today` : `${noun} until turned back on`;
};

/**
 * Why the visibility flags keep this item off the menu right now, or `null` when they do not.
 * The decision is `filterVisibility`'s; this only names the flag responsible for it.
 */
export const adminHiddenReason = (item, now = moment()) => {
  if (!item || filterVisibility(item, now)) {
    return null;
  }

  if (!item.active) {
    return item.hideDays && item.hideDays.length > 0 ? 'Hidden today' : 'Hidden from menu';
  }
  if (item.hideForWeb === true) {
    return 'Hidden from the web menu';
  }
  if (isInHiddenWindow(item, now)) {
    return 'Hidden during its set hours';
  }
  if (item.hidden_until) {
    return `Hidden until ${new Date(item.hidden_until).toLocaleString()}`;
  }
  return 'Outside its show-only hours';
};

/**
 * @returns {null|{live: {reason: string, until: string|null}|null, hidden: string|null}} `null` when
 * the dish is on the menu, otherwise both halves so the caller can label them separately.
 */
export const describeOffShelf = (item, offShelfMap, now = moment()) => {
  if (!item) {
    return null;
  }

  const entry = offShelfMap && item.id ? offShelfMap.get(item.id) : undefined;
  const live = entry ? { reason: offShelfReason(entry), until: entry.effectiveDate || null } : null;
  const hidden = adminHiddenReason(item, now);

  return live || hidden ? { live, hidden } : null;
};

export const isOffShelf = (item, offShelfMap, now = moment()) =>
  describeOffShelf(item, offShelfMap, now) !== null;

export const countOffShelf = (categories, offShelfMap, now = moment()) =>
  (categories || []).reduce(
    (count, category) =>
      count + (category.items || []).filter((item) => isOffShelf(item, offShelfMap, now)).length,
    0
  );
